import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import BodyText from 'components/widget/BodyText';
import { FullWidthPageContent } from 'layouts/SingleColumnLayout';

import LensPlanOptions from 'components/widgets/LensPlanOptions';
import IncludeSolutionWidget from 'components/widgets/IncludeSolutionWidget';
import Button from '../Button';
import InfoBox from '../widget/InfoBox';

const Wrapper = styled.div`
  .topSection {
    margin-bottom: ${({ theme }) => theme.spacing * 3}px;
  }

  .formCta {
    padding: ${({ theme }) => theme.spacing * 2}px;
    > * {
      margin-bottom: ${({ theme }) => theme.spacing * 2}px;
    }
  }

  .solutionCheckBox {
    margin-top: ${({ theme }) => theme.spacing}px;
  }
`;

const ChangeLensWidget = ({
  className,
  plans,
  onSubmit,
  onCancel,
  onChange,
  solution,
}) => {
  const classes = ['ChangeLensWidget'];
  const defaultPlan = plans.find((p) => p.selected);
  const defaultPlanId = defaultPlan && defaultPlan.id;
  const [planId, setPlanId] = React.useState(defaultPlanId);
  const [withSolution, setWithSolution] = React.useState(
    solution && solution.selected,
  );

  React.useEffect(() => {
    setPlanId(defaultPlanId);
  }, [defaultPlanId]);

  React.useEffect(() => {
    if (solution) {
      setWithSolution(solution.selected);
    }
  }, [solution]);

  function handleChange(value) {
    setPlanId(value);
    if (onChange) onChange(value);
  }

  function handleSolutionChange(e) {
    setWithSolution(e.target.checked);
  }

  if (className) classes.push(className);

  function handleSubmit(e) {
    e.preventDefault();
    onSubmit({
      planId,
      withSolution,
    });
  }

  const canSubmit = (planId !== defaultPlanId)
    || withSolution !== (solution && solution.selected);

  return (
    <Wrapper className={classes.join(' ')}>
      <div className="topSection">
        <h1 className="no_margin_top">Change your package</h1>

        <BodyText>
          Choose what you would like to receive every&nbsp;
          <strong>3 months</strong>
        </BodyText>
      </div>

      <FullWidthPageContent as="form" onSubmit={handleSubmit}>
        <LensPlanOptions
          plans={plans}
          onChange={handleChange}
          dataLayerAction="Change Lens Amount"
        />
        {solution && (
          <IncludeSolutionWidget
            className="solutionCheckBox"
            solution={solution}
            selected={withSolution}
            onChange={handleSolutionChange}
            frequency="mo."
          />
        )}
        <div className="formCta">
          <Button
            disabled={!canSubmit}
            fullWidth
            type="submit"
            dataLayer={{
              action: 'Change Lens Amount',
              label: 'Review Changes',
            }}
          >
            Review your changes
          </Button>
          <Button inverse fullWidth type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </FullWidthPageContent>
      <InfoBox>
        We can only amend deliveries up to 14 days prior to dispatch. If your
        delivery is already processing, your requested changes will take effect
        from the following scheduled delivery.
      </InfoBox>
    </Wrapper>
  );
};

ChangeLensWidget.displayName = 'ChangeLensWidget';

ChangeLensWidget.propTypes = {
  className: PropTypes.string,
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      subTitle: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      selected: PropTypes.bool,
    }),
  ).isRequired,
  solution: PropTypes.shape({
    selected: PropTypes.bool,
  }),
  onChange: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

ChangeLensWidget.defaultProps = {
  className: null,
  onChange: undefined,
  solution: undefined,
};

export default ChangeLensWidget;
